import { compilePublicPathPattern, fillPublicPathPattern, normalizePublicPathPattern, parsePublicPathPattern, } from "../pathPattern.js";
export { fillPublicPathPattern, normalizePublicPathPattern, } from "../pathPattern.js";
export function definePublicRouteLoaders(loaders) {
    return loaders;
}
export async function executePublicRouteLoader(resolution, loaders, options = {}) {
    const loaderKey = getPublicRouteLoaderKey(resolution.route);
    if (!loaderKey)
        return {
            kind: "ok",
            data: {},
        };
    const loader = loaders[loaderKey];
    if (!loader)
        throw new Error(`Missing public route loader '${loaderKey}'.`);
    const url = normalizePublicRouteDataPath(options.url ?? resolution.routePath);
    const queryIndex = url.indexOf("?");
    return loader({
        pathname: resolution.routePath,
        url,
        searchParams: new URLSearchParams(queryIndex === -1 ? "" : url.slice(queryIndex + 1)),
        route: resolution.route,
        params: resolution.params,
        signal: options.signal,
    });
}
export function getPublicRouteLoaderKey(route) {
    return route.data?.loaderKey
        ?? (route.delivery.kind === "dynamic" ? route.delivery.loaderKey : null);
}
export function getPublicRouteCachePolicy(route) {
    return route.delivery.kind === "spa-shell"
        ? { kind: "no-store" }
        : route.delivery.cache;
}
export function shouldPrerenderPublicRoute(route) {
    return route.render.kind === "ssg"
        || (route.render.kind === "hybrid" && route.render.prerender === "all");
}
export function resolvePublicLoaderCanonicalPath(site, resolution, candidate) {
    if (candidate === undefined)
        return resolution.routePath;
    const canonicalPath = site.normalizePath(candidate);
    const canonicalResolution = site.resolvePath(canonicalPath);
    if (canonicalResolution.type !== "render"
        || canonicalResolution.route.key !== resolution.route.key
        || canonicalResolution.locale !== resolution.locale)
        throw new Error(`Loader canonicalPath '${candidate}' must resolve the same route and locale as '${resolution.routePath}'.`);
    return canonicalResolution.routePath;
}
export function getPublicSiteCapabilityDiagnostics(site, capabilities) {
    const diagnostics = [];
    const definitions = [
        ...site.routeDefinitions,
        ...site.patternRouteDefinitions,
    ];
    for (const route of definitions) {
        const loaderKey = route.data?.loaderKey
            ?? (route.delivery.kind === "dynamic" ? route.delivery.loaderKey : null);
        if (loaderKey && !capabilities.loaders?.[loaderKey])
            diagnostics.push(`loader '${loaderKey}' for route '${route.key}'`);
        if (route.headResolverKey && !capabilities.headResolvers?.[route.headResolverKey])
            diagnostics.push(`head resolver '${route.headResolverKey}' for route '${route.key}'`);
    }
    return diagnostics;
}
export function assertPublicSiteCapabilities(site, capabilities) {
    const diagnostics = getPublicSiteCapabilityDiagnostics(site, capabilities);
    if (diagnostics.length > 0)
        throw new Error(`Missing public site capabilities:\n${diagnostics.join("\n")}`);
}
export function getPublicMarkdownCapabilityDiagnostics(site, capabilities) {
    const diagnostics = [];
    const requiredRendererKeys = new Set();
    const definitions = [
        ...site.routeDefinitions,
        ...site.patternRouteDefinitions,
    ].filter((route) => capabilities.includeRoute?.(route) ?? true);
    for (const route of definitions) {
        switch (route.markdown.kind) {
            case "renderer": {
                const key = route.markdown.key;
                requiredRendererKeys.add(key);
                if (typeof capabilities.renderers?.[key] !== "function")
                    diagnostics.push(`markdown renderer '${key}' for route '${route.key}'`);
                break;
            }
            case "content":
                if (!capabilities.hasContent?.(route.markdown.key, route))
                    diagnostics.push(`markdown content '${route.markdown.key}' for route '${route.key}'`);
                break;
            case "seo":
                break;
        }
    }
    if (capabilities.rejectUnusedRenderers)
        for (const key of Object.keys(capabilities.renderers ?? {}))
            if (!requiredRendererKeys.has(key))
                diagnostics.push(`undeclared markdown renderer '${key}'`);
    return diagnostics;
}
export function assertPublicMarkdownCapabilities(site, capabilities) {
    const diagnostics = getPublicMarkdownCapabilityDiagnostics(site, capabilities);
    if (diagnostics.length > 0)
        throw new Error(`Missing public Markdown capabilities:\n${diagnostics.join("\n")}`);
}
export function getPublicIndexablePathsByRouteKeys(site, routeKeys) {
    const routeByKey = new Map(site.routeDefinitions.map((route) => [route.key, route]));
    const paths = new Set();
    for (const key of routeKeys) {
        const route = routeByKey.get(key);
        if (!route)
            throw new Error(`Unknown concrete public route key '${key}'.`);
        for (const path of site.getIndexablePaths(route.path))
            paths.add(path);
    }
    return [...paths];
}
function normalizePublicPathname(rawPath) {
    const pathname = rawPath || "/";
    const normalizedPath = pathname.startsWith("/") ? pathname : `/${pathname}`;
    if (normalizedPath.length > 1 && normalizedPath.endsWith("/"))
        return normalizedPath.slice(0, -1);
    return normalizedPath;
}
export function normalizePublicPath(rawPath) {
    const pathWithoutQuery = (rawPath || "/").split("?")[0]?.split("#")[0] ?? "/";
    return normalizePublicPathname(pathWithoutQuery);
}
export function normalizePublicRouteDataPath(rawUrl) {
    const urlWithoutHash = (rawUrl || "/").split("#")[0] ?? "/";
    const queryIndex = urlWithoutHash.indexOf("?");
    const pathname = normalizePublicPath(queryIndex === -1 ? urlWithoutHash : urlWithoutHash.slice(0, queryIndex));
    if (queryIndex === -1)
        return pathname;
    const searchParams = new URLSearchParams(urlWithoutHash.slice(queryIndex + 1));
    searchParams.sort();
    const query = searchParams.toString();
    return query ? `${pathname}?${query}` : pathname;
}
function buildLocaleByCode(locales) {
    return new Map(locales.map((locale) => [locale.code.toLowerCase(), locale.locale]));
}
function buildLocaleCodeByCode(locales) {
    return new Map(locales.map((locale) => [locale.code.toLowerCase(), locale.code]));
}
function isLocalizedPolicy(policy) {
    return policy !== "none";
}
function isIndexableSeo(seo) {
    return seo.indexing?.kind !== "noindex";
}
const DefaultNoStoreCache = { kind: "no-store" };
function normalizeLocalizedPaths(paths) {
    if (!paths)
        return undefined;
    return Object.fromEntries(Object.entries(paths).map(([localeCode, path]) => [
        localeCode.toLowerCase(),
        normalizePublicPath(path),
    ]));
}
function normalizeLocalizedPathPatterns(patterns) {
    if (!patterns)
        return undefined;
    return Object.fromEntries(Object.entries(patterns).map(([localeCode, pattern]) => [
        localeCode.toLowerCase(),
        normalizePublicPathPattern(pattern),
    ]));
}
function inferPublicRenderPolicy(delivery) {
    switch (delivery?.kind) {
        case "static":
            return { kind: "ssg" };
        case "dynamic":
            return { kind: "ssr" };
        case "spa-shell":
            return { kind: "spa" };
        case "hybrid-app-entry":
            return {
                kind: "hybrid",
                prerender: "all",
                fallback: "ssr",
            };
        default:
            return { kind: "ssg" };
    }
}
function inferPublicDelivery(delivery, render, data, cache) {
    if (delivery)
        return delivery;
    if (render.kind === "spa")
        return { kind: "spa-shell" };
    if (data)
        return {
            kind: "dynamic",
            loaderKey: data.loaderKey,
            cache: cache ?? DefaultNoStoreCache,
        };
    if (render.kind === "hybrid")
        return {
            kind: "hybrid-app-entry",
            browserPublicPaths: "all",
            cache: cache ?? DefaultNoStoreCache,
        };
    return {
        kind: "static",
        cache: cache ?? DefaultNoStoreCache,
    };
}
function normalizeRoutePolicies(definition) {
    const access = definition.access ?? "public";
    const render = definition.render ?? inferPublicRenderPolicy(definition.delivery);
    const data = definition.data ?? (definition.delivery?.kind === "dynamic"
        ? { loaderKey: definition.delivery.loaderKey }
        : undefined);
    const delivery = inferPublicDelivery(definition.delivery, render, data, definition.cache);
    const seo = access === "authenticated" && definition.seo.indexing === undefined
        ? {
            ...definition.seo,
            indexing: {
                kind: "noindex",
                reason: "private",
                follow: false,
            },
        }
        : definition.seo;
    return {
        access,
        crawl: definition.crawl ?? (access === "authenticated" ? "disallow" : "allow"),
        render,
        data,
        delivery,
        seo,
        llms: definition.llms ?? false,
    };
}
function flattenPublicRoutes(routes) {
    const flatRoutes = [];
    const patternRoutes = [];
    const routerOnlyPatternRoutes = [];
    const redirects = [];
    for (const definition of routes) {
        if (definition.kind === "redirect") {
            redirects.push({
                path: normalizePublicPath(definition.path),
                location: normalizePublicPath(definition.location),
                statusCode: definition.statusCode ?? 308,
            });
            continue;
        }
        const localePolicy = definition.localePolicy;
        const policies = normalizeRoutePolicies(definition.kind === "collection"
            ? {
                ...definition,
                seo: definition.indexSeo,
                llms: definition.indexLlms,
            }
            : definition);
        if (definition.kind === "page") {
            flatRoutes.push({
                kind: "page",
                key: definition.key,
                path: normalizePublicPath(definition.path),
                componentId: definition.componentId,
                browserComponentId: definition.browserComponentId,
                localePolicy,
                localizedPaths: normalizeLocalizedPaths(definition.localizedPaths),
                ...policies,
                headResolverKey: definition.headResolverKey,
                contentKey: definition.contentKey,
                name: definition.name,
                sitemap: definition.sitemap ?? false,
                markdown: definition.markdown ?? { kind: "seo" },
            });
            continue;
        }
        if (definition.kind === "dynamic-page") {
            patternRoutes.push({
                kind: "dynamic-page",
                key: definition.key,
                pathPattern: normalizePublicPathPattern(definition.pathPattern),
                componentId: definition.componentId,
                localePolicy,
                localizedPathPatterns: normalizeLocalizedPathPatterns(definition.localizedPathPatterns),
                ...policies,
                resourceSource: definition.resources?.source
                    ?? (definition.sitemap && "kind" in definition.sitemap
                        ? definition.sitemap.source
                        : undefined),
                headResolverKey: definition.headResolverKey,
                sitemap: definition.sitemap ?? false,
                markdown: definition.markdown ?? { kind: "seo" },
            });
            continue;
        }
        const indexPath = normalizePublicPath(definition.indexPath);
        flatRoutes.push({
            kind: "collection-index",
            key: definition.key,
            path: indexPath,
            componentId: definition.indexComponentId,
            localePolicy,
            localizedPaths: normalizeLocalizedPaths(definition.indexLocalizedPaths),
            ...policies,
            contentKey: definition.indexContentKey,
            seo: policies.access === "authenticated" && definition.indexSeo.indexing === undefined
                ? policies.seo
                : definition.indexSeo,
            sitemap: definition.indexSitemap ?? false,
            collectionKey: definition.key,
            markdown: definition.indexMarkdown ?? { kind: "seo" },
            llms: definition.indexLlms ?? policies.llms,
        });
        routerOnlyPatternRoutes.push({
            kind: "collection-item",
            key: definition.key,
            pathPattern: normalizePublicPathPattern(definition.itemPathPattern),
            componentId: definition.itemComponentId,
            localePolicy,
            localizedPathPatterns: normalizeLocalizedPathPatterns(definition.itemLocalizedPathPatterns),
            ...policies,
            headResolverKey: definition.itemHeadResolverKey,
            seo: definition.itemSeo ?? policies.seo,
            sitemap: false,
            markdown: { kind: "seo" },
            llms: false,
        });
        for (const item of definition.items) {
            const itemPath = normalizePublicPath(definition.getItemPath(item));
            const itemSeo = definition.getItemSeo(item);
            const itemPattern = compilePublicPathPattern(definition.itemPathPattern);
            const itemMatch = itemPattern.regex.exec(itemPath);
            if (!itemMatch)
                throw new Error(`Public collection '${definition.key}' item path '${itemPath}' does not match '${definition.itemPathPattern}'.`);
            const itemParams = Object.fromEntries(itemPattern.parameterNames.map((name, index) => [
                name,
                decodeURIComponent(itemMatch[index + 1] ?? ""),
            ]));
            const localizedPaths = definition.itemLocalizedPathPatterns
                ? Object.fromEntries(Object.entries(definition.itemLocalizedPathPatterns).map(([localeCode, pattern]) => [
                    localeCode,
                    fillPublicPathPattern(pattern, itemParams),
                ]))
                : undefined;
            flatRoutes.push({
                kind: "collection-item",
                key: `${definition.key}.${definition.getItemKey(item)}`,
                path: itemPath,
                componentId: definition.itemComponentId,
                localePolicy,
                localizedPaths: normalizeLocalizedPaths(localizedPaths),
                ...policies,
                headResolverKey: definition.itemHeadResolverKey,
                contentKey: definition.getItemContentKey?.(item) ?? undefined,
                seo: itemSeo,
                sitemap: definition.getItemSitemap?.(item) ?? false,
                collectionKey: definition.key,
                markdown: definition.getItemMarkdown?.(item) ?? { kind: "seo" },
                llms: definition.getItemLlms?.(item) ?? policies.llms,
            });
        }
    }
    return {
        flatRoutes,
        patternRoutes,
        routerOnlyPatternRoutes,
        redirects,
    };
}
function buildValidationDiagnostics(options, flatRoutes, collectionRoutes) {
    const diagnostics = [];
    const routeKeys = new Set();
    const routePaths = new Set();
    const localizedRoutePaths = new Set();
    const collectionPatterns = new Set();
    const collectionPatternShapes = new Set();
    const sitemapCollectionSources = new Set();
    const resourceCollectionSources = new Set();
    const localeCodes = new Set(options.locales.map((locale) => locale.code));
    if (options.locales.length === 0)
        diagnostics.push({
            severity: "error",
            code: "locales.empty",
            message: "Public site must declare at least one locale.",
        });
    if (!localeCodes.has(options.defaultLocaleCode))
        diagnostics.push({
            severity: "error",
            code: "locales.default_code_missing",
            message: `Default locale code '${options.defaultLocaleCode}' is not present in locales.`,
        });
    for (const route of flatRoutes) {
        if (routeKeys.has(route.key))
            diagnostics.push({
                severity: "error",
                code: "route.duplicate_key",
                message: `Duplicate public route key '${route.key}'.`,
            });
        routeKeys.add(route.key);
        if (routePaths.has(route.path))
            diagnostics.push({
                severity: "error",
                code: "route.duplicate_path",
                message: `Duplicate public route path '${route.path}'.`,
            });
        routePaths.add(route.path);
        if (route.localePolicy === "none" && route.localizedPaths)
            diagnostics.push({
                severity: "error",
                code: "route.unlocalized_with_localized_paths",
                message: `Unlocalized route '${route.key}' cannot declare localized paths.`,
            });
        if (route.localePolicy !== "none")
            for (const locale of options.locales) {
                const localizedPath = route.localizedPaths?.[locale.code.toLowerCase()] ?? route.path;
                const localizedKey = `${locale.code.toLowerCase()}|${localizedPath}`;
                if (localizedRoutePaths.has(localizedKey))
                    diagnostics.push({
                        severity: "error",
                        code: "route.duplicate_localized_path",
                        message: `Duplicate localized route path '/${locale.code}${localizedPath === "/" ? "" : localizedPath}'.`,
                    });
                localizedRoutePaths.add(localizedKey);
            }
        if (!route.path.startsWith("/"))
            diagnostics.push({
                severity: "error",
                code: "route.invalid_path",
                message: `Public route '${route.key}' must start with '/'.`,
            });
        if (route.delivery.kind === "hybrid-app-entry"
            && route.delivery.browserPublicPaths === "localized-only"
            && route.localePolicy === "none")
            diagnostics.push({
                severity: "error",
                code: "route.invalid_browser_public_paths",
                message: `Public route '${route.key}' cannot use browserPublicPaths 'localized-only' without a localized locale policy.`,
            });
        if (!isIndexableRoute(route) && route.sitemap)
            diagnostics.push({
                severity: "error",
                code: "route.non_indexable_sitemap",
                message: `Public route '${route.key}' is not indexable but is present in the sitemap.`,
            });
        if (route.data
            && route.delivery.kind === "dynamic"
            && route.data.loaderKey !== route.delivery.loaderKey)
            diagnostics.push({
                severity: "error",
                code: "route.loader_mismatch",
                message: `Public route '${route.key}' declares conflicting loader keys.`,
            });
        for (const localeCode of Object.keys(route.localizedPaths ?? {}))
            if (![...localeCodes].some((code) => code.toLowerCase() === localeCode.toLowerCase()))
                diagnostics.push({
                    severity: "error",
                    code: "route.unknown_localized_path_locale",
                    message: `Public route '${route.key}' declares a path for unknown locale '${localeCode}'.`,
                });
    }
    for (const collectionRoute of collectionRoutes) {
        if (collectionRoute.kind === "dynamic-page") {
            if (routeKeys.has(collectionRoute.key))
                diagnostics.push({
                    severity: "error",
                    code: "route.duplicate_key",
                    message: `Duplicate public route key '${collectionRoute.key}'.`,
                });
            routeKeys.add(collectionRoute.key);
        }
        if (collectionPatterns.has(collectionRoute.pathPattern))
            diagnostics.push({
                severity: "error",
                code: "collection.duplicate_pattern",
                message: `Duplicate public collection path pattern '${collectionRoute.pathPattern}'.`,
            });
        collectionPatterns.add(collectionRoute.pathPattern);
        const patternShape = parsePublicPathPattern(collectionRoute.pathPattern).segments
            .map((segment) => segment.kind === "parameter" ? ":" : segment.value)
            .join("/");
        if (collectionPatternShapes.has(patternShape))
            diagnostics.push({
                severity: "error",
                code: "route.ambiguous_pattern",
                message: `Public route pattern '${collectionRoute.pathPattern}' conflicts with another dynamic pattern.`,
            });
        collectionPatternShapes.add(patternShape);
        if (collectionRoute.localePolicy === "none" && collectionRoute.localizedPathPatterns)
            diagnostics.push({
                severity: "error",
                code: "route.unlocalized_with_localized_paths",
                message: `Unlocalized route '${collectionRoute.key}' cannot declare localized path patterns.`,
            });
        const baseParameters = compilePublicPathPattern(collectionRoute.pathPattern).parameterNames;
        for (const [localeCode, localizedPattern] of Object.entries(collectionRoute.localizedPathPatterns ?? {})) {
            const localizedParameters = compilePublicPathPattern(localizedPattern).parameterNames;
            if (localizedParameters.length !== baseParameters.length
                || localizedParameters.some((name, index) => name !== baseParameters[index]))
                diagnostics.push({
                    severity: "error",
                    code: "route.localized_parameters_mismatch",
                    message: `Localized pattern '${localizedPattern}' for route '${collectionRoute.key}' (${localeCode}) must use parameters '${baseParameters.join(", ")}'.`,
                });
        }
        if (!collectionRoute.pathPattern.includes(":"))
            diagnostics.push({
                severity: "warning",
                code: "collection.static_pattern",
                message: `Public collection '${collectionRoute.key}' item pattern has no dynamic segment.`,
            });
        if (!isIndexableRoute(collectionRoute) && collectionRoute.sitemap)
            diagnostics.push({
                severity: "error",
                code: "route.non_indexable_sitemap",
                message: `Public route '${collectionRoute.key}' is not indexable but declares a sitemap collection.`,
            });
        if (collectionRoute.sitemap && "kind" in collectionRoute.sitemap) {
            if (collectionRoute.resourceSource
                && collectionRoute.resourceSource !== collectionRoute.sitemap.source)
                diagnostics.push({
                    severity: "error",
                    code: "resource.source_mismatch",
                    message: `Public route '${collectionRoute.key}' declares different resource and sitemap sources.`,
                });
            if (sitemapCollectionSources.has(collectionRoute.sitemap.source))
                diagnostics.push({
                    severity: "error",
                    code: "sitemap.duplicate_collection_source",
                    message: `Sitemap collection source '${collectionRoute.sitemap.source}' is used by multiple routes.`,
                });
            sitemapCollectionSources.add(collectionRoute.sitemap.source);
        }
        if (collectionRoute.resourceSource) {
            if (!/^[a-z0-9][a-z0-9-]*$/.test(collectionRoute.resourceSource))
                diagnostics.push({
                    severity: "error",
                    code: "resource.invalid_source",
                    message: `Public resource source '${collectionRoute.resourceSource}' has an invalid name.`,
                });
            if (resourceCollectionSources.has(collectionRoute.resourceSource))
                diagnostics.push({
                    severity: "error",
                    code: "resource.duplicate_source",
                    message: `Public resource source '${collectionRoute.resourceSource}' is used by multiple routes.`,
                });
            resourceCollectionSources.add(collectionRoute.resourceSource);
        }
        for (const localeCode of Object.keys(collectionRoute.localizedPathPatterns ?? {}))
            if (![...localeCodes].some((code) => code.toLowerCase() === localeCode.toLowerCase()))
                diagnostics.push({
                    severity: "error",
                    code: "route.unknown_localized_path_locale",
                    message: `Public route '${collectionRoute.key}' declares a path for unknown locale '${localeCode}'.`,
                });
    }
    const redirects = options.routes.filter((route) => (route.kind === "redirect"));
    for (const redirect of redirects) {
        const path = normalizePublicPath(redirect.path);
        const location = normalizePublicPath(redirect.location);
        if (routeKeys.has(redirect.key))
            diagnostics.push({
                severity: "error",
                code: "route.duplicate_key",
                message: `Duplicate public route key '${redirect.key}'.`,
            });
        routeKeys.add(redirect.key);
        if (routePaths.has(path) || collectionPatterns.has(path))
            diagnostics.push({
                severity: "error",
                code: "route.duplicate_path",
                message: `Duplicate public route path '${path}'.`,
            });
        routePaths.add(path);
        if (path === location)
            diagnostics.push({
                severity: "error",
                code: "redirect.self",
                message: `Public redirect '${redirect.key}' redirects to itself.`,
            });
    }
    return diagnostics;
}
function assertNoValidationErrors(diagnostics) {
    const errors = diagnostics.filter((diagnostic) => diagnostic.severity === "error");
    if (errors.length === 0)
        return;
    throw new Error(errors.map((error) => `[${error.code}] ${error.message}`).join("\n"));
}
function isIndexableRoute(route) {
    return route.access === "public"
        && route.render.kind !== "spa"
        && isIndexableSeo(route.seo);
}
export function createPublicSite(options) {
    const localeByCode = buildLocaleByCode(options.locales);
    const localeCodeByCode = buildLocaleCodeByCode(options.locales);
    const { flatRoutes, patternRoutes, routerOnlyPatternRoutes, redirects, } = flattenPublicRoutes(options.routes);
    const routerPatternRoutes = [
        ...patternRoutes,
        ...routerOnlyPatternRoutes,
    ];
    const diagnostics = buildValidationDiagnostics(options, flatRoutes, routerPatternRoutes);
    assertNoValidationErrors(diagnostics);
    const routesByPath = new Map();
    for (const definition of flatRoutes) {
        routesByPath.set(`|${definition.path}`, definition);
        if (definition.localePolicy !== "none")
            for (const locale of options.locales) {
                const localePath = definition.localizedPaths?.[locale.code.toLowerCase()]
                    ?? definition.path;
                routesByPath.set(`${locale.code.toLowerCase()}|${localePath}`, definition);
            }
    }
    const redirectByPath = new Map(redirects.map((redirect) => [redirect.path, redirect]));
    const patternRouteMatchers = patternRoutes.flatMap((definition) => {
        const patterns = [{
                localeCode: null,
                pathPattern: definition.pathPattern,
            }];
        if (definition.localePolicy !== "none")
            for (const locale of options.locales)
                patterns.push({
                    localeCode: locale.code.toLowerCase(),
                    pathPattern: definition.localizedPathPatterns?.[locale.code.toLowerCase()]
                        ?? definition.pathPattern,
                });
        return patterns.map(({ localeCode, pathPattern }) => ({
            definition,
            localeCode,
            pathPattern,
            ...compilePublicPathPattern(pathPattern),
        }));
    });
    const getLocaleCodeFromPath = (rawPath) => {
        const [, firstSegment] = normalizePublicPath(rawPath).split("/");
        if (!firstSegment)
            return null;
        return localeCodeByCode.get(firstSegment.toLowerCase()) ?? null;
    };
    const getLocaleFromPath = (rawPath) => {
        const [, firstSegment] = normalizePublicPath(rawPath).split("/");
        if (!firstSegment)
            return null;
        return localeByCode.get(firstSegment.toLowerCase()) ?? null;
    };
    const removeLocalePrefix = (rawPath) => {
        const normalizedPath = normalizePublicPath(rawPath);
        const [, firstSegment] = normalizedPath.split("/");
        if (!firstSegment || !localeByCode.has(firstSegment.toLowerCase()))
            return normalizedPath;
        const withoutPrefix = normalizedPath.slice(firstSegment.length + 1);
        return withoutPrefix.startsWith("/")
            ? withoutPrefix || "/"
            : `/${withoutPrefix}`;
    };
    const withLocalePrefix = (rawPath, localeCode) => {
        const normalizedPath = normalizePublicPath(rawPath);
        const suffix = normalizedPath === "/" ? "" : normalizedPath;
        return `/${localeCode}${suffix}`;
    };
    const matchRoute = (rawPath) => {
        const normalizedPath = normalizePublicPath(rawPath);
        const localeCode = getLocaleCodeFromPath(normalizedPath);
        const publicPath = normalizePublicPath(removeLocalePrefix(normalizedPath));
        const localeKey = localeCode?.toLowerCase() ?? "";
        const staticDefinition = routesByPath.get(`${localeKey}|${publicPath}`);
        if (staticDefinition)
            return {
                route: staticDefinition,
                params: {},
            };
        for (const matcher of patternRouteMatchers) {
            if (matcher.localeCode !== (localeCode?.toLowerCase() ?? null))
                continue;
            const match = matcher.regex.exec(publicPath);
            if (!match)
                continue;
            const definition = matcher.definition;
            const params = Object.fromEntries(matcher.parameterNames.map((name, index) => {
                const rawValue = match[index + 1] ?? "";
                try {
                    return [name, decodeURIComponent(rawValue)];
                }
                catch {
                    return [name, rawValue];
                }
            }));
            const logicalPath = fillPublicPathPattern(definition.pathPattern, params);
            return {
                route: {
                    kind: definition.kind,
                    key: definition.key,
                    path: logicalPath,
                    componentId: definition.componentId,
                    localePolicy: definition.localePolicy,
                    localizedPaths: definition.localizedPathPatterns,
                    delivery: definition.delivery,
                    access: definition.access,
                    crawl: definition.crawl,
                    render: definition.render,
                    data: definition.data,
                    headResolverKey: definition.headResolverKey,
                    seo: definition.seo,
                    sitemap: definition.sitemap,
                    collectionKey: definition.kind === "collection-item"
                        ? definition.key
                        : undefined,
                    markdown: definition.markdown,
                    llms: definition.llms,
                },
                params,
            };
        }
        return null;
    };
    const getRouteDefinition = (rawPath) => {
        return matchRoute(rawPath)?.route ?? null;
    };
    const getLocalizedRoutePath = (route, params, localeCode) => {
        const localizedPath = route.localizedPaths?.[localeCode.toLowerCase()];
        return localizedPath
            ? fillPublicPathPattern(localizedPath, params)
            : route.path;
    };
    const isKnownRoute = (rawPath) => {
        const normalizedPath = normalizePublicPath(rawPath);
        const locale = getLocaleFromPath(normalizedPath);
        const definition = getRouteDefinition(normalizedPath);
        if (!definition)
            return false;
        if (definition.localePolicy === "none")
            return locale === null && definition.path === normalizedPath;
        if (locale)
            return true;
        return definition.localePolicy.unlocalized === "indexable";
    };
    const isAppShellRoute = (rawPath) => {
        const normalizedPath = normalizePublicPath(rawPath);
        const route = matchRoute(normalizedPath)?.route;
        return route?.render.kind === "spa";
    };
    const getRedirectTarget = (rawPath, localeCode = options.defaultLocaleCode) => {
        const normalizedPath = normalizePublicPath(rawPath);
        const locale = getLocaleFromPath(normalizedPath);
        const match = matchRoute(normalizedPath);
        const definition = match?.route ?? null;
        if (!definition || locale || definition.localePolicy === "none")
            return null;
        if (definition.localePolicy.unlocalized !== "redirect")
            return null;
        return withLocalePrefix(getLocalizedRoutePath(definition, match?.params ?? {}, localeCode), localeCode);
    };
    const resolveRedirectStatus = (route, statusCode) => {
        if (statusCode)
            return statusCode;
        if (route
            && route.localePolicy !== "none"
            && route.localePolicy.unlocalized === "redirect"
            && route.localePolicy.redirect === "accept-language")
            return 307;
        return 308;
    };
    const resolveRedirectVary = (route) => (route
        && route.localePolicy !== "none"
        && route.localePolicy.unlocalized === "redirect"
        && route.localePolicy.redirect === "accept-language"
        ? "Accept-Language"
        : undefined);
    const resolvePath = (rawPath, context = {}) => {
        const normalizedPath = normalizePublicPath(rawPath);
        const explicitRedirect = redirectByPath.get(normalizedPath);
        if (explicitRedirect)
            return {
                type: "redirect",
                location: explicitRedirect.location,
                statusCode: explicitRedirect.statusCode,
            };
        const locale = getLocaleFromPath(normalizedPath);
        const match = matchRoute(normalizedPath);
        const route = match?.route ?? null;
        const publicPath = route?.path ?? normalizePublicPath(removeLocalePrefix(normalizedPath));
        const needsLocalizedRedirect = route !== null
            && locale === null
            && route.localePolicy !== "none"
            && route.localePolicy.unlocalized === "redirect";
        if (needsLocalizedRedirect)
            return {
                type: "redirect",
                location: withLocalePrefix(getLocalizedRoutePath(route, match?.params ?? {}, context.redirectLocaleCode ?? options.defaultLocaleCode), context.redirectLocaleCode ?? options.defaultLocaleCode),
                statusCode: resolveRedirectStatus(route, context.redirectStatusCode),
                vary: resolveRedirectVary(route),
            };
        const routeIsAvailable = route !== null && (route.localePolicy === "none"
            ? locale === null && route.path === normalizedPath
            : locale !== null || route.localePolicy.unlocalized === "indexable");
        if (!routeIsAvailable || !route || !match) {
            return {
                type: "not-found",
                routePath: normalizedPath,
            };
        }
        if (route.render.kind === "spa" && route.access === "authenticated")
            return {
                type: "app-shell",
                routePath: normalizedPath,
            };
        const routeLocale = locale ?? options.defaultLocale;
        return {
            type: "render",
            route,
            params: match.params,
            locale: routeLocale,
            routePath: normalizedPath,
            publicPath,
            canonicalPath: normalizedPath,
            indexable: isIndexableRoute(route),
        };
    };
    const shouldApplyHreflang = (rawPath) => {
        const route = getRouteDefinition(rawPath);
        return route?.localePolicy !== "none";
    };
    const getXDefaultPath = (rawPath) => {
        const match = matchRoute(rawPath);
        const definition = match?.route ?? null;
        const publicPath = definition?.path ?? normalizePublicPath(removeLocalePrefix(rawPath));
        if (!definition || definition.localePolicy === "none")
            return publicPath;
        if (definition.localePolicy.xDefault === "unlocalized" && definition.localePolicy.unlocalized === "indexable")
            return publicPath;
        return withLocalePrefix(getLocalizedRoutePath(definition, match?.params ?? {}, options.defaultLocaleCode), options.defaultLocaleCode);
    };
    const getAlternatePaths = (rawPath) => {
        const match = matchRoute(rawPath);
        const definition = match?.route;
        if (!definition || definition.localePolicy === "none")
            return [];
        return [
            ...options.locales.map((locale) => ({
                hreflang: locale.code,
                path: withLocalePrefix(getLocalizedRoutePath(definition, match.params, locale.code), locale.code),
            })),
            {
                hreflang: "x-default",
                path: getXDefaultPath(rawPath),
            },
        ];
    };
    const buildAlternateHrefs = (rawPath, siteUrl) => {
        const normalizedSiteUrl = siteUrl.replace(/\/$/, "");
        return getAlternatePaths(rawPath).map((alternate) => ({
            ...alternate,
            href: `${normalizedSiteUrl}${alternate.path}`,
        }));
    };
    const getRouteIndexablePaths = (definition, params = {}) => {
        if (!isIndexableRoute(definition))
            return [];
        if (definition.localePolicy === "none")
            return [definition.path];
        const paths = definition.localePolicy.unlocalized === "indexable"
            ? [definition.path]
            : [];
        for (const locale of options.locales)
            paths.push(withLocalePrefix(getLocalizedRoutePath(definition, params, locale.code), locale.code));
        return paths;
    };
    const getIndexablePaths = (rawPath) => {
        const match = matchRoute(rawPath);
        return match
            ? getRouteIndexablePaths(match.route, match.params)
            : [];
    };
    const getPrerenderPaths = () => {
        const paths = new Set();
        for (const definition of flatRoutes) {
            if (!shouldPrerenderPublicRoute(definition))
                continue;
            for (const path of getRouteIndexablePaths(definition))
                paths.add(path);
        }
        return Array.from(paths);
    };
    const getSitemapEntries = () => {
        const entries = [];
        for (const definition of flatRoutes) {
            if (!definition.sitemap || "kind" in definition.sitemap)
                continue;
            for (const path of getRouteIndexablePaths(definition))
                entries.push({
                    path,
                    lastmod: definition.sitemap.lastmod ?? options.sitemapLastModified,
                    changefreq: definition.sitemap.changefreq,
                    priority: definition.sitemap.priority,
                });
        }
        return entries;
    };
    const getSitemapCollections = () => (patternRoutes.flatMap((definition) => {
        const sitemap = definition.sitemap;
        if (!sitemap || !("kind" in sitemap))
            return [];
        return [{
                key: definition.key,
                source: sitemap.source,
                pathPattern: definition.pathPattern,
                componentId: definition.componentId,
                localePolicy: definition.localePolicy,
                changefreq: sitemap.changefreq,
                priority: sitemap.priority,
            }];
    }));
    const getResourceCollections = () => (patternRoutes.flatMap((definition) => {
        if (!definition.resourceSource)
            return [];
        const sitemap = definition.sitemap && "kind" in definition.sitemap
            ? definition.sitemap
            : null;
        return [{
                key: definition.key,
                source: definition.resourceSource,
                pathPattern: definition.pathPattern,
                componentId: definition.componentId,
                localePolicy: definition.localePolicy,
                changefreq: sitemap?.changefreq,
                priority: sitemap?.priority,
            }];
    }));
    const getIndexableUrls = () => {
        const paths = new Set();
        for (const definition of flatRoutes)
            for (const path of getRouteIndexablePaths(definition))
                paths.add(path);
        return Array.from(paths);
    };
    const getRedirectAliases = () => {
        const aliases = [...redirects];
        for (const definition of flatRoutes) {
            if (definition.localePolicy === "none" || definition.localePolicy.unlocalized !== "redirect")
                continue;
            aliases.push({
                path: definition.path,
                location: withLocalePrefix(getLocalizedRoutePath(definition, {}, options.defaultLocaleCode), options.defaultLocaleCode),
                statusCode: resolveRedirectStatus(definition),
                vary: resolveRedirectVary(definition),
            });
        }
        for (const definition of patternRoutes) {
            if (definition.localePolicy === "none" || definition.localePolicy.unlocalized !== "redirect")
                continue;
            const localizedPattern = definition.localizedPathPatterns?.[options.defaultLocaleCode.toLowerCase()] ?? definition.pathPattern;
            aliases.push({
                path: definition.pathPattern,
                location: withLocalePrefix(localizedPattern, options.defaultLocaleCode),
                statusCode: resolveRedirectStatus(definition),
                vary: resolveRedirectVary(definition),
            });
        }
        return aliases;
    };
    const describeRoutes = () => flatRoutes.map((definition) => ({
        key: definition.key,
        path: definition.path,
        componentId: definition.componentId,
        localePolicy: definition.localePolicy,
        delivery: definition.delivery.kind,
        indexablePaths: getRouteIndexablePaths(definition),
        redirectAliases: getRedirectAliases().filter((alias) => alias.path === definition.path),
    }));
    const validate = () => diagnostics.slice();
    const manifestOrderByKey = new Map(options.routes.map((route, index) => [route.key, index]));
    const routerRouteDefinitions = [
        ...flatRoutes.filter((route) => route.kind !== "collection-item"),
        ...routerPatternRoutes,
    ].map((definition, index) => ({ definition, index }))
        .sort((left, right) => ((manifestOrderByKey.get(left.definition.key) ?? Number.MAX_SAFE_INTEGER)
        - (manifestOrderByKey.get(right.definition.key) ?? Number.MAX_SAFE_INTEGER)
        || left.index - right.index))
        .map(({ definition }) => definition);
    return {
        origin: options.origin?.replace(/\/$/, ""),
        robots: options.robots ?? {},
        locales: options.locales,
        defaultLocale: options.defaultLocale,
        defaultLocaleCode: options.defaultLocaleCode,
        routeDefinitions: flatRoutes,
        patternRouteDefinitions: patternRoutes,
        routerRouteDefinitions,
        redirectDefinitions: redirects,
        normalizePath: normalizePublicPath,
        getLocaleFromPath,
        getLocaleCodeFromPath,
        removeLocalePrefix,
        withLocalePrefix,
        getRouteDefinition,
        matchRoute,
        isKnownRoute,
        isAppShellRoute,
        getRedirectTarget,
        resolvePath,
        shouldApplyHreflang,
        getXDefaultPath,
        getAlternatePaths,
        buildAlternateHrefs,
        getIndexablePaths,
        getPrerenderPaths,
        getSitemapEntries,
        getSitemapCollections,
        getResourceCollections,
        getIndexableUrls,
        getRedirectAliases,
        describeRoutes,
        validate,
    };
}
//# sourceMappingURL=index.js.map